'use client'

import React from 'react'
import { TranslationsProvider } from '@eo-locale/react'
import type { TranslationError } from '@eo-locale/core'
import {
  getTwoLetterISOLanguageName,
  MicrositeSettings,
  RequestServerContext,
  UserSettings,
} from '@dustin-web/microsite-config'

import { AuthProvider } from '@dustin-web/microsite-components/src/auth'
import { LayoutProvider, QueryStateProvider } from '@dustin-web/microsite-components/src/hooks'
import { UserSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-user-settings'
import { SiteContextProvider } from '@dustin-web/microsite-components/src/hooks/use-site-context'
import { MicrositeSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-microsite-settings'
import { CookiesContext } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import { SWRConfig } from 'swr'
import { PortalProvider } from '@dustin-web/microsite-components/src/hooks/portal'
import { useFavoritesSwr } from '@dustin-web/microsite-data/src/hooks/use-favorites-swr'
import { useShoppingCartSWR } from '@dustin-web/microsite-data/src/hooks/use-shopping-cart-swr'
import { DataLoaderHooksProvider } from '@dustin-web/product-components/src/hooks/dataloader/use-dataloader-hooks'
import useCompareSwr from '@dustin-web/microsite-data/src/hooks/use-comparisons-swr'
import useNotifyAddToCart from '@dustin-web/microsite-data/src/hooks/use-notify-cart-action'
import useNotifyClick from '@dustin-web/microsite-data/src/hooks/use-notify-click-action'
import { AssistiveCartProvider } from '@dustin-web/microsite-components/src/hooks/use-assistive-cart-context'

import useCategoryNavigation from '@dustin-web/microsite-data/src/hooks/use-category-navigation-swr'
import { useAccountSwr } from '@dustin-web/microsite-data/src/hooks/use-account-swr'
import { FeatureFlagsProvider } from '@dustin-web/microsite-data/src/feature-flags/client'

let hasIntlErrors = false
const onIntlError = (intlError: TranslationError) => {
  if (!hasIntlErrors) {
    if (
      intlError.message.includes('[eo-locale] id missing') ||
      intlError.message.includes('Translation failed')
    ) {
      // eslint-disable-next-line no-console
      console.warn(intlError.message, 'Further intl errors will be suppressed.')
      hasIntlErrors = true

      return
    }

    throw intlError
  }
}

type SwrRootProps = {
  fallback:
  | {
    [key: string]: unknown
  }
  | undefined
  requestContext: RequestServerContext
  userSettings: UserSettings
  micrositeSettings: MicrositeSettings
  cookies: { [key: string]: string }
  messages: { [key: string]: string }
  query?: { [key: string]: string }
  headers?: NodeJS.Dict<string | string[]>
  optimizelyDatafile?: string
  optimizelySdkKey?: string
  initialAuthenticated?: boolean
  children: React.ReactNode
}

const SwrRoot = ({
  fallback = {},
  requestContext,
  userSettings,
  micrositeSettings,
  cookies,
  messages = {},
  query = {},
  optimizelyDatafile,
  initialAuthenticated,
  children,
}: SwrRootProps) => {
  const twoLetterISOLanguageName = getTwoLetterISOLanguageName(userSettings.language)

  // We are only loading one language so no on-the-fly switching of locales allowed
  const locales = [
    {
      language: twoLetterISOLanguageName,
      messages,
    },
  ]

  const dataLoaderHooks = React.useMemo(() => {
    return {
      favorites: useFavoritesSwr,
      shoppingcart: useShoppingCartSWR,
      compare: useCompareSwr,
      account: useAccountSwr,
      categoryNavigation: useCategoryNavigation,
      notifyAddToCart: useNotifyAddToCart,
      notifyClick: useNotifyClick,
    }
  }, [])

  return (
    <MicrositeSettingsContext.Provider value={micrositeSettings}>
      <SiteContextProvider value={requestContext}>
        <CookiesContext.Provider value={cookies}>
          <UserSettingsContext.Provider value={userSettings}>
            <AuthProvider config={micrositeSettings.authConfig} initialAuthenticated={initialAuthenticated}>
                <FeatureFlagsProvider datafile={optimizelyDatafile}>
                  <QueryStateProvider query={query}>
                    <SWRConfig value={{ fallback }}>
                      <TranslationsProvider
                        onError={onIntlError}
                        language={twoLetterISOLanguageName}
                        locales={locales}>
                        <LayoutProvider>
                          <PortalProvider>
                            <AssistiveCartProvider>
                              <DataLoaderHooksProvider dataLoaderHooks={dataLoaderHooks}>
                                {children}
                              </DataLoaderHooksProvider>
                            </AssistiveCartProvider>
                          </PortalProvider>
                        </LayoutProvider>
                      </TranslationsProvider>
                    </SWRConfig>
                  </QueryStateProvider>
                </FeatureFlagsProvider>
            </AuthProvider>
          </UserSettingsContext.Provider>
        </CookiesContext.Provider>
      </SiteContextProvider>
    </MicrositeSettingsContext.Provider>
  )
}

export default SwrRoot
